import {
  ListEventsRequest,
  GetEventRequest,
  EventStatusFilter,
  EventAudience,
  Event,
  RegisterForEventRequest,
  RegisterForEventResponse,
  CancelMyRegistrationRequest,
  CancelMyRegistrationResponse,
  GetMyRegistrationRequest,
  GetMyRegistrationResponse,
} from "../generated/events/v1/event_pb";
import { EventServiceClient } from "../generated/events/v1/EventServiceClientPb";
import { getGrpcEndpoint } from "./grpcEndpoint";

const GRPC_ENDPOINT = getGrpcEndpoint();

// Create client instance
const client = new EventServiceClient(GRPC_ENDPOINT);

export type RegisterForEventResult = RegisterForEventResponse.AsObject;
export type CancelMyRegistrationResult = CancelMyRegistrationResponse.AsObject;
export type GetMyRegistrationResult = GetMyRegistrationResponse.AsObject;

/**
 * Public Event Service Client
 * Provides methods to fetch events from the Railway service
 */
class PublicEventService {
  /**
   * List kids-audience events with optional status filter
   * @param status - Filter by event status (all, current, future, past)
   * @returns Promise with list of events
   */
  async listEvents(
    status: "all" | "current" | "future" | "past" = "all"
  ): Promise<Event.AsObject[]> {
    return new Promise((resolve, reject) => {
      const request = new ListEventsRequest();

      // Map status string to EventStatusFilter enum
      const statusMap = {
        all: EventStatusFilter.STATUS_FILTER_ALL,
        current: EventStatusFilter.STATUS_FILTER_CURRENT,
        future: EventStatusFilter.STATUS_FILTER_FUTURE,
        past: EventStatusFilter.STATUS_FILTER_PAST,
      };

      request.setStatus(statusMap[status]);
      request.setAudience(EventAudience.EVENT_AUDIENCE_KIDS);

      client.listEvents(request, null, (err, response) => {
        if (err) {
          console.error("Error fetching events:", err);
          reject(err);
          return;
        }

        const events = response
          .getEventsList()
          .map((event) => event.toObject());
        resolve(events);
      });
    });
  }

  /**
   * Get a single event by ID
   * @param id - Event ID
   * @returns Promise with event data
   */
  async getEvent(id: string): Promise<Event.AsObject> {
    return new Promise((resolve, reject) => {
      const request = new GetEventRequest();
      request.setId(id);

      client.getEvent(request, null, (err, response) => {
        if (err) {
          console.error("Error fetching event:", err);
          reject(err);
          return;
        }

        resolve(response.toObject());
      });
    });
  }

  /**
   * Register for a free event (confirmed or waitlisted).
   */
  async registerForEvent(input: {
    eventId: string;
    name: string;
    email: string;
    phone?: string;
    partySize?: number;
  }): Promise<RegisterForEventResult> {
    const request = new RegisterForEventRequest();
    request.setEventId(input.eventId);
    request.setName(input.name.trim());
    request.setEmail(input.email.trim());
    if (input.phone?.trim()) {
      request.setPhone(input.phone.trim());
    }
    request.setPartySize(
      input.partySize && input.partySize > 0 ? input.partySize : 1
    );

    try {
      const response = await client.registerForEvent(request, null);
      return response.toObject();
    } catch (err) {
      console.error("Error registering for event:", err);
      throw err;
    }
  }

  /**
   * Cancel own registration by event id + email.
   */
  async cancelMyRegistration(input: {
    eventId: string;
    email: string;
  }): Promise<CancelMyRegistrationResult> {
    const request = new CancelMyRegistrationRequest();
    request.setEventId(input.eventId);
    request.setEmail(input.email.trim());

    try {
      const response = await client.cancelMyRegistration(request, null);
      return response.toObject();
    } catch (err) {
      console.error("Error cancelling registration:", err);
      throw err;
    }
  }

  /**
   * Look up registration status by event id + email.
   */
  async getMyRegistration(input: {
    eventId: string;
    email: string;
  }): Promise<GetMyRegistrationResult> {
    const request = new GetMyRegistrationRequest();
    request.setEventId(input.eventId);
    request.setEmail(input.email.trim());

    try {
      const response = await client.getMyRegistration(request, null);
      return response.toObject();
    } catch (err) {
      console.error("Error looking up registration:", err);
      throw err;
    }
  }
}

// Export singleton instance
export const eventService = new PublicEventService();

// Legacy callback-based function for backward compatibility
export function fetchEventsFromGrpc(
  onSuccess: (response: any) => void,
  onError: (error: any) => void
) {
  eventService
    .listEvents("all")
    .then((events) => {
      onSuccess({ events });
    })
    .catch((err) => {
      onError(err);
    });
}
